import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ClipboardList, CreditCard, FileText, Search, Plus, Eye } from 'lucide-react';
import { FormInput, FormSelect, FormTextarea } from './shared/FormComponents';
import { LoadingButton } from './shared/LoadingComponents';
import { BUTTON_STYLES, CARD_STYLES, BILLING_TYPES } from '../utils/constants';
import { useToast } from '../../../hooks/useToast';
import { appointmentAPI, prescriptionAPI, billingAPI } from '../../../api';
import { bankMasterService } from '../../../api/accounting';
import { printInvoice, downloadInvoicePDF } from '../../receptionist-dashboard/utils/invoicePrint';

const Billing = ({ loading }) => {
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  const [billingData, setBillingData] = useState({
    patient: '',
    service: '',
    paymentMethod: '',
    amount: '',
    notes: '',
    discount: '',
    tax: ''
  });

  const [invoiceData, setInvoiceData] = useState({
    patientName: '',
    services: [],
    totalAmount: 0,
    paidAmount: 0,
    balanceAmount: 0
  });

  // Appointments (Completed) for invoice-from-prescription flow
  const [apptsLoading, setApptsLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState('');
  const selectedAppointment = useMemo(() => (
    appointments.find(a => (a._id || a.id) === selectedAppointmentId) || null
  ), [appointments, selectedAppointmentId]);

  // Prescriptions for selected appointment/patient
  const [prescriptions, setPrescriptions] = useState([]);
  const [prescriptionsLoading, setPrescriptionsLoading] = useState(false);
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState('');

  // Invoice creation form
  const [invoiceForm, setInvoiceForm] = useState({ billingType: 'Patient', taxRate: 18, notes: '' });

  // Wizard modal & stepper state
  const [showWizard, setShowWizard] = useState(false);
  const [step, setStep] = useState(1); // 1: Select + Preview, 2: Select items & Create, 3: Payment

  // Preview data & selection
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [patientId, setPatientId] = useState('');
  const [includeDoctorCharge, setIncludeDoctorCharge] = useState(true);
  const [selectedMedicines, setSelectedMedicines] = useState(new Set());
  const [selectedTests, setSelectedTests] = useState(new Set());
  const [selectedImagingTests, setSelectedImagingTests] = useState(new Set());
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  // Doctor charge prompt when preview fails due to missing consultation fee
  const [showDoctorChargePrompt, setShowDoctorChargePrompt] = useState(false);
  const [pendingPreviewParams, setPendingPreviewParams] = useState(null);

  // Created invoice & payment
  const [createdInvoice, setCreatedInvoice] = useState(null); // store invoice object
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: 'Cash',
    amount: '',
    bankAccountId: '',
    transactionId: '',
    reference: '',
    notes: ''
  });
  const [addingPayment, setAddingPayment] = useState(false);

  // Invoices list (main table)
  const [invLoading, setInvLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invPage, setInvPage] = useState(1);
  const [invTotalPages, setInvTotalPages] = useState(1);
  const [invLimit] = useState(20);
  const [invSearch, setInvSearch] = useState('');

  // Pay-from-list modal state
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [payForm, setPayForm] = useState({ paymentMethod: 'Cash', amount: '', bankAccountId: '', transactionId: '', reference: '', notes: '' });
  const [payLoading, setPayLoading] = useState(false);

  // Invoice preview modal state
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [duplicateCopy, setDuplicateCopy] = useState(false);

  const openInvoicePreview = async (invoiceId) => {
    try {
      setInvoicePreviewLoading(true);
      setShowInvoicePreview(true);
      let data = null;
      try {
        data = await billingAPI.getInvoicePreviewById(invoiceId);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) {
          // Fallback: build preview locally from invoice + doctor charges info
          const inv = await billingAPI.getInvoiceById(invoiceId);
          // Normalize invoice shape defensively
          const invoiceRaw = inv?.data?.invoice || inv?.data || inv;

          // Helper: safe number
          const n = (v, d = 0) => {
            const num = Number(v);
            return Number.isFinite(num) ? num : d;
          };

          // Extract patient
          const patient = invoiceRaw?.patient || invoiceRaw?.patientId || {};
          const patientName = `${patient?.firstName || patient?.name || ''} ${patient?.lastName || ''}`.trim() || patient?.fullName || '';
          const pAddr = patient?.personalAddress || patient?.address || {};
          const address = [
            pAddr?.formatted,
            pAddr?.street,
            pAddr?.street1 || pAddr?.streetLine1 || pAddr?.addressLine1,
            pAddr?.street2 || pAddr?.streetLine2 || pAddr?.addressLine2,
            pAddr?.area,
            pAddr?.city,
            pAddr?.state,
            pAddr?.zipCode || pAddr?.postalCode || pAddr?.pincode,
            pAddr?.country,
          ].filter(Boolean).join(', ');

          // Extract doctor info from various shapes
          const doctorObj = invoiceRaw?.doctor || invoiceRaw?.appointment?.doctor || invoiceRaw?.doctorInfo || null;
          const inferredDoctorId = doctorObj?._id || invoiceRaw?.doctorId || invoiceRaw?.appointment?.doctorId || invoiceRaw?.doctorCharges?.doctorId || null;
          let doctorInfo = null;
          if (doctorObj) {
            doctorInfo = {
              name: [doctorObj.firstName, doctorObj.lastName].filter(Boolean).join(' ') || doctorObj.name || '',
              registrationNumber: doctorObj.licenseNumber || doctorObj.registrationNumber || '',
              department: doctorObj.department || doctorObj.departmentName || '',
              specialization: doctorObj.specialization || doctorObj.speciality || ''
            };
          } else if (inferredDoctorId) {
            try {
              const docCharges = await billingAPI.getDoctorCharges(inferredDoctorId);
              const doc = docCharges?.doctor || docCharges?.data?.doctor || null;
              if (doc) {
                doctorInfo = {
                  name: [doc.firstName, doc.lastName].filter(Boolean).join(' ') || doc.name || '',
                  registrationNumber: doc.licenseNumber || '',
                  department: doc.department || '',
                  specialization: doc.specialization || ''
                };
              }
            } catch (_) {
              // ignore doctor fetch failures
            }
          }

          // Items: support multiple shapes
          const items = Array.isArray(invoiceRaw?.items) ? invoiceRaw.items : (Array.isArray(invoiceRaw?.lineItems) ? invoiceRaw.lineItems : []);
          const medList = Array.isArray(invoiceRaw?.medicines) && invoiceRaw.medicines.length
            ? invoiceRaw.medicines
            : items.filter(i => {
                const t = (i?.type || i?.itemType || i?.category || i?.kind || '').toString().toLowerCase();
                return t.includes('medicine') || t.includes('drug');
              }).map(i => ({
                medicineName: i.name || i.medicineName,
                quantity: n(i.quantity, 1),
                unitPrice: n(i.unitPrice ?? i.price, 0),
                total: n(i.total ?? (n(i.unitPrice ?? i.price, 0) * n(i.quantity, 1)), 0)
              }));

          const testList = Array.isArray(invoiceRaw?.tests) && invoiceRaw.tests.length
            ? invoiceRaw.tests
            : items.filter(i => {
                const t = (i?.type || i?.itemType || i?.category || i?.kind || '').toString().toLowerCase();
                return t.includes('test') || t.includes('lab') || t.includes('diagnostic');
              }).map(i => ({
                testName: i.name || i.testName,
                quantity: n(i.quantity, 1),
                unitPrice: n(i.unitPrice ?? i.price, 0),
                total: n(i.total ?? (n(i.unitPrice ?? i.price, 0) * n(i.quantity, 1)), 0)
              }));

          // Doctor charge: prefer explicit field else infer from items
          const explicitDoc = invoiceRaw?.doctorCharges;
          let doctorCharges = null;
          if (explicitDoc) {
            doctorCharges = {
              consultationType: explicitDoc.consultationType || explicitDoc.serviceName || 'Consultation',
              amount: n(explicitDoc.amount, 0),
              taxRate: n(explicitDoc.taxRate, 0),
              taxAmount: n(explicitDoc.taxAmount, 0),
              total: n(explicitDoc.total ?? explicitDoc.amount, 0)
            };
          } else {
            const consultItem = items.find(i => {
              const t = (i?.type || i?.itemType || i?.category || i?.kind || '').toString().toLowerCase();
              return t.includes('consult') || t.includes('doctor');
            });
            if (consultItem) {
              doctorCharges = {
                consultationType: consultItem.name || 'Consultation',
                amount: n(consultItem.unitPrice ?? consultItem.price ?? consultItem.amount, 0),
                taxRate: n(consultItem.taxRate, 0),
                taxAmount: n(consultItem.taxAmount, 0),
                total: n(consultItem.total ?? (n(consultItem.unitPrice ?? consultItem.price ?? consultItem.amount, 0) * n(consultItem.quantity, 1)), 0)
              };
            }
          }

          // Totals
          const subtotalComputed = n(invoiceRaw?.subtotal, 0) || (n(doctorCharges?.total, 0) + medList.reduce((s, m) => s + n(m.total, 0), 0) + testList.reduce((s, t) => s + n(t.total, 0), 0));
          const discount = n(invoiceRaw?.totalDiscount ?? invoiceRaw?.discount, 0);
          const tax = n(invoiceRaw?.totalTax ?? invoiceRaw?.tax, 0);
          const grandTotal = n(invoiceRaw?.grandTotal ?? invoiceRaw?.total, 0) || (subtotalComputed - discount + tax);
          const payments = Array.isArray(invoiceRaw?.payments) ? invoiceRaw.payments : (Array.isArray(invoiceRaw?.paymentHistory) ? invoiceRaw.paymentHistory : []);
          const amountPaid = n(invoiceRaw?.amountPaid, 0) || payments.filter(p => (p.status || '').toLowerCase() === 'completed').reduce((s, p) => s + n(p.amount, 0), 0);
          const amountDue = n(invoiceRaw?.amountDue, 0) || Math.max(grandTotal - amountPaid, 0);

          // Payment summary
          const lastCompleted = payments.filter(p => (p.status || '').toLowerCase() === 'completed').slice(-1)[0];
          const creator = invoiceRaw?.createdBy || invoiceRaw?.createdUser || null;
          const collectedBy = lastCompleted?.collectedBy || lastCompleted?.processedBy || creator || null;

          // Compose fallback preview structure similar to API
          const aptObj = invoiceRaw?.appointment || null;
          let aptDateTime = aptObj?.dateTime || ((aptObj?.appointmentDate && aptObj?.appointmentTime) ? `${aptObj.appointmentDate}T${aptObj.appointmentTime}` : aptObj?.appointmentDate) || invoiceRaw?.appointmentDate || null;
          let aptId = (aptObj?._id || invoiceRaw?.appointmentId || (Array.isArray(invoiceRaw?.appointmentIds) && invoiceRaw.appointmentIds[0]) || aptObj?.id || '')?.toString?.() || null;
          // If not available, try fetching the appointment directly
          if (!aptDateTime && aptId) {
            try {
              const aptRes = await appointmentAPI.getById(aptId);
              const apt = aptRes?.data || aptRes;
              aptDateTime = apt?.dateTime || ((apt?.appointmentDate && apt?.appointmentTime) ? `${apt.appointmentDate}T${apt.appointmentTime}` : apt?.appointmentDate) || null;
            } catch (_) {}
          }
          data = {
            invoice: {
              id: (invoiceRaw?._id || '').toString(),
              invoiceNumber: invoiceRaw?.invoiceNumber || invoiceRaw?._id,
              date: invoiceRaw?.invoiceDate || invoiceRaw?.createdAt || null,
              status: invoiceRaw?.status,
              notes: invoiceRaw?.internalNotes || invoiceRaw?.notes || '',
              totals: {
                subtotal: n(subtotalComputed, 0),
                discount: n(discount, 0),
                tax: n(tax, 0),
                grandTotal: n(grandTotal, 0),
                amountPaid: n(amountPaid, 0),
                amountDue: n(amountDue, 0)
              }
            },
            patient: {
              id: (patient?._id || patient?.id || '').toString(),
              externalId: patient?.externalId || '',
              name: patientName || '-',
              phone: patient?.mobile || patient?.phone || '',
              email: patient?.email || '',
              address
            },
            doctor: doctorInfo,
            items: {
              doctorCharges: doctorCharges,
              medicines: (medList || []).map(m => ({
                name: m.medicineName,
                quantity: n(m.quantity, 1),
                unitPrice: n(m.unitPrice, 0),
                total: n(m.total, 0)
              })),
              tests: (testList || []).map(t => ({
                name: t.testName,
                quantity: n(t.quantity, 1),
                unitPrice: n(t.unitPrice, 0),
                total: n(t.total, 0)
              }))
            },
            payment: {
              mode: lastCompleted?.paymentMethod || lastCompleted?.mode || null,
              due: n(amountDue, 0),
              collectedBy: collectedBy ? {
                name: [collectedBy.firstName, collectedBy.lastName].filter(Boolean).join(' ') || collectedBy.name || ''
              } : null
            },
            appointment: {
              id: aptId,
              dateTime: aptDateTime || null
            },
            source: 'fallback'
          };
        } else {
          throw err;
        }
      }
      setInvoicePreview(data);
    } catch (e) {
      console.error('Failed to load invoice preview:', e);
      showError(e?.response?.data?.message || e?.message || 'Failed to load invoice preview');
      setShowInvoicePreview(false);
    } finally {
      setInvoicePreviewLoading(false);
    }
  };

  // Payment method options per backend validator
  const PAYMENT_METHOD_OPTIONS = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Card', label: 'Card' },
    { value: 'UPI', label: 'UPI' },
    { value: 'Net Banking', label: 'Net Banking' },
    { value: 'Cheque', label: 'Cheque' },
    { value: 'Insurance', label: 'Insurance' },
    { value: 'Other', label: 'Other' },
  ];

  // Helpers: Selection totals from preview (use backend-calculated totalPrice)
  const selectedTotals = useMemo(() => {
    if (!previewData) return { doctor: 0, medicines: 0, tests: 0, imagingTests: 0, grand: 0 };
    const doc = includeDoctorCharge && previewData.doctorCharges ? Number(previewData.doctorCharges.amount || 0) : 0;
    const meds = (previewData.medicines || []).reduce((sum, m) => {
      if (!selectedMedicines.has((m.medicineId || m._id || '').toString())) return sum;
      return sum + Number(m.totalPrice || 0);
    }, 0);
    const tests = (previewData.tests || []).reduce((sum, t) => {
      if (!selectedTests.has((t.testId || t._id || '').toString())) return sum;
      return sum + Number(t.totalPrice || 0);
    }, 0);
    const imagingTests = (previewData.imagingTests || []).reduce((sum, t) => {
      if (!selectedImagingTests.has((t.imagingTestId || t._id || '').toString())) return sum;
      return sum + Number(t.totalPrice || 0);
    }, 0);
    const grand = doc + meds + tests + imagingTests;
    return { doctor: doc, medicines: meds, tests, imagingTests, grand };
  }, [previewData, includeDoctorCharge, selectedMedicines, selectedTests, selectedImagingTests]);

  // Load recent completed appointments for quick billing
  useEffect(() => {
    const loadCompletedAppointments = async () => {
      setApptsLoading(true);
      try {
        const res = await appointmentAPI.list({
          status: 'Completed',
          includePatient: true,
          includeDoctor: true,
          limit: 50,
        });
  const items = res?.data?.items || res?.items || res?.data || [];
        // Normalize and sort by date desc client-side
        const toDate = (apt) => {
          const dt = apt?.dateTime || (apt?.appointmentDate && apt?.appointmentTime ? `${apt.appointmentDate}T${apt.appointmentTime}` : apt?.appointmentDate);
          return dt ? new Date(dt) : null;
        };
        const sorted = Array.isArray(items) ? [...items].sort((a, b) => {
          const at = toDate(a)?.getTime() || 0;
          const bt = toDate(b)?.getTime() || 0;
          return bt - at;
        }) : [];
        setAppointments(sorted);
      } catch (e) {
        console.error('Failed to load completed appointments:', e);
        showError(e?.response?.data?.message || e?.message || 'Failed to load completed appointments');
        setAppointments([]);
      } finally {
        setApptsLoading(false);
      }
    };
    loadCompletedAppointments();
  }, []);

  // Load invoices for table
  const fetchInvoices = async (page = 1) => {
    setInvLoading(true);
    try {
      const res = await billingAPI.listInvoices({ page, limit: invLimit, sort: '-invoiceDate', search: invSearch || undefined, q: invSearch || undefined });
      const data = res?.data || res;
      const items = data?.items || data?.data?.items || [];
      const pagination = data?.pagination || data?.data?.pagination || {};
      setInvoices(items);
      setInvPage(pagination.page || page);
      setInvTotalPages(pagination.pages || 1);
    } catch (e) {
      console.error('Failed to load invoices:', e);
      showError(e?.response?.data?.message || e?.message || 'Failed to load invoices');
      setInvoices([]);
    } finally {
      setInvLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load prescriptions for the selected appointment/patient
  useEffect(() => {
    const fetchPrescriptions = async () => {
      setPrescriptions([]);
      setSelectedPrescriptionId('');
      if (!selectedAppointment) return;
      setPrescriptionsLoading(true);
      try {
        // Prefer prescriptions embedded on appointment if available
        const embedded = selectedAppointment.prescriptions;
        if (Array.isArray(embedded) && embedded.length) {
          setPrescriptions(embedded);
          return;
        }
        // Fallback: fetch by patient and try to filter around appointment date
        const patientId = selectedAppointment.patient?._id || selectedAppointment.patient?.id || selectedAppointment.patientId;
        if (!patientId) {
          setPrescriptions([]);
          return;
        }
        const res = await prescriptionAPI.getPatientPrescriptions(patientId, { limit: 100 });
        // Normalize various backend shapes to an array of prescriptions
        const tryExtract = (r) => {
          if (Array.isArray(r)) return r;
          if (Array.isArray(r?.data)) return r.data;
          if (Array.isArray(r?.items)) return r.items;
          if (Array.isArray(r?.data?.items)) return r.data.items;
          if (Array.isArray(r?.prescriptions?.items)) return r.prescriptions.items;
          if (Array.isArray(r?.data?.prescriptions?.items)) return r.data.prescriptions.items;
          return [];
        };
        const items = tryExtract(res);
        const arr = Array.isArray(items) ? items : [];
        // If prescriptions have appointment reference, filter by that
        const aptId = selectedAppointment._id || selectedAppointment.id;
        let filtered = arr.filter(p => (p.appointmentId || p.appointment)?.toString?.() === aptId);
        if (!filtered.length) {
          // Otherwise, select those created close to appointment date (within 24h)
          const aptDate = new Date(selectedAppointment.dateTime || selectedAppointment.appointmentDate || selectedAppointment.createdAt || Date.now());
          const dayMs = 24 * 60 * 60 * 1000;
          filtered = arr.filter(p => {
            const createdAt = new Date(p.createdAt || p.date || 0);
            return Math.abs(createdAt.getTime() - aptDate.getTime()) <= dayMs;
          });
        }
        setPrescriptions(filtered.length ? filtered : arr);
      } catch (e) {
        console.error('Failed to load prescriptions for appointment:', e);
        setPrescriptions([]);
      } finally {
        setPrescriptionsLoading(false);
      }
    };
    fetchPrescriptions();
  }, [selectedAppointment]);

  // Step 1: Fetch preview
  const fetchPreview = async () => {
    if (!selectedAppointmentId) return showError('Please select an appointment');
    if (!selectedPrescriptionId) return showError('Please select a prescription');
    const apt = selectedAppointment;
    const doctorId = apt?.doctor?._id || apt?.doctorId || apt?.doctor;
    const appointmentId = apt?._id || apt?.id || selectedAppointmentId;
    setPreviewLoading(true);
    try {
      const res = await billingAPI.getPreview({ appointmentId, prescriptionId: selectedPrescriptionId, doctorId });
      const data = res?.data || res; // unwrap helper already used, but defensive
      console.log('📥 Preview data received:', {
        medicines: data?.medicines?.length || 0,
        tests: data?.tests?.length || 0,
        imagingTests: data?.imagingTests?.length || 0,
        imagingTestsSample: data?.imagingTests?.[0]
      });
      setPreviewData(data);
      setPatientId(data?.patientId || apt?.patient?._id || apt?.patientId || '');
      // initialize selections
      setIncludeDoctorCharge(Boolean(data?.doctorCharges));
      setSelectedMedicines(new Set((data?.medicines || []).map(m => (m.medicineId || m._id || '').toString())));
      setSelectedTests(new Set((data?.tests || []).map(t => (t.testId || t._id || '').toString())));
      setSelectedImagingTests(new Set((data?.imagingTests || []).map(t => (t.imagingTestId || t._id || '').toString())));
      console.log('✅ Selections initialized:', {
        medicines: (data?.medicines || []).length,
        tests: (data?.tests || []).length,
        imagingTests: (data?.imagingTests || []).length,
        selectedImagingTests: Array.from(new Set((data?.imagingTests || []).map(t => (t.imagingTestId || t._id || '').toString())))
      });
      setStep(2);
      showSuccess('Preview ready');
    } catch (e) {
      console.error('Failed to get billing preview:', e);
      const msg = e?.response?.data?.message || e?.message || '';
      const missingCharge = msg.toLowerCase().includes('no follow-up consultation charge found') || msg.toLowerCase().includes('consultation charge');
      if (missingCharge) {
        // Store params and prompt user to proceed with zero consultation fee
        setPendingPreviewParams({ appointmentId, prescriptionId: selectedPrescriptionId, doctorId });
        setShowDoctorChargePrompt(true);
      } else {
        showError(msg || 'Failed to get billing preview');
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmPreviewWithoutDoctorCharge = async () => {
    if (!pendingPreviewParams) { setShowDoctorChargePrompt(false); return; }
    const { appointmentId, prescriptionId, doctorId } = pendingPreviewParams;
    setPreviewLoading(true);
    try {
      // Retry preview while hinting backend to skip doctor charge (defensive: may be ignored)
      const res = await billingAPI.getPreview({ appointmentId, prescriptionId, doctorId, skipDoctorCharge: true });
      const data = res?.data || res;
      // Force doctor charge to be treated as zero on client if backend still returns it missing
      const normalized = { ...data };
      if (normalized.doctorCharges) {
        normalized.doctorCharges = { ...normalized.doctorCharges, amount: 0 };
      }
      setPreviewData(normalized);
      setPatientId(normalized?.patientId || selectedAppointment?.patient?._id || selectedAppointment?.patientId || '');
      // Do not include doctor charge by default
      setIncludeDoctorCharge(false);
      setSelectedMedicines(new Set((normalized?.medicines || []).map(m => (m.medicineId || m._id || '').toString())));
      setSelectedTests(new Set((normalized?.tests || []).map(t => (t.testId || t._id || '').toString())));
      setSelectedImagingTests(new Set((normalized?.imagingTests || []).map(t => (t.imagingTestId || t._id || '').toString())));
      setStep(2);
      setShowDoctorChargePrompt(false);
      setPendingPreviewParams(null);
      showWarning('Proceeding without consultation fee (set to ₹0)');
    } catch (e) {
      console.error('Retry preview without doctor charge failed:', e);
      showError(e?.response?.data?.message || e?.message || 'Failed to get preview');
      setShowDoctorChargePrompt(false);
      setPendingPreviewParams(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Step 2: Create bill from selected items
  const handleCreateFromSelection = async () => {
    try {
      if (!previewData) return showError('No preview available');
      if (!patientId) return showError('Missing patient information');

      const appointmentId = previewData.appointmentId || selectedAppointment?._id || selectedAppointment?.id;
      const payload = {
        patientId,
        billingType: invoiceForm.billingType || 'Patient',
        taxRate: Number(invoiceForm.taxRate || 18),
        prescriptionId: previewData.prescriptionId || selectedPrescriptionId,
        appointmentId,
        selectedDoctorCharges: includeDoctorCharge && previewData.doctorCharges ? {
          doctorId: previewData.doctorCharges.doctorId,
          consultationType: previewData.doctorCharges.consultationType,
          amount: Number(previewData.doctorCharges.amount)
        } : undefined,
        selectedMedicines: Array.from(selectedMedicines),
        selectedTests: Array.from(selectedTests),
        selectedImagingTests: Array.from(selectedImagingTests),
        additionalServices: [],
      };

      if (!payload.selectedDoctorCharges && payload.selectedMedicines.length === 0 && payload.selectedTests.length === 0 && payload.selectedImagingTests.length === 0) {
        return showError('Select at least one item (doctor, medicine, test, or imaging test)');
      }

      console.log('📤 Sending billing payload:', {
        ...payload,
        selectedMedicinesCount: payload.selectedMedicines.length,
        selectedTestsCount: payload.selectedTests.length,
        selectedImagingTestsCount: payload.selectedImagingTests.length
      });
      console.log('📤 Imaging tests array:', payload.selectedImagingTests);
      console.log('📤 Full payload JSON:', JSON.stringify(payload, null, 2));

      setCreatingInvoice(true);
      const res = await billingAPI.createFromSelection(payload);
      const inv = res?.data || res;
      setCreatedInvoice(inv);
      // Calculate grand total with tax from selected items
      const grandTotalWithTax = selectedTotals.grand * (1 + Number(invoiceForm.taxRate||0)/100);
      const amountPaid = Number(inv?.amountPaid || 0);
      const calculatedDue = grandTotalWithTax - amountPaid;
      setPaymentForm(prev => ({ ...prev, amount: calculatedDue.toFixed(2) }));
      showSuccess('Invoice created');
      setStep(3);
    } catch (e) {
      console.error('Failed to create invoice:', e);
      showError(e?.response?.data?.message || e?.message || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  // Step 3: Load bank accounts when needed
  useEffect(() => {
    const needsBank = !['Cash', 'Insurance'].includes(paymentForm.paymentMethod);
    if (step === 3 && needsBank) {
      (async () => {
        try {
          setBankLoading(true);
          const res = await bankMasterService.getAll({ status: 'active', limit: 100 });
          // Normalize possible paginate structures: docs/items/records -> []
          const list = Array.isArray(res) ? res : (res?.docs || res?.items || res?.records || []);
          const normalized = list.map(acc => ({
            ...acc,
            displayName: acc.displayName || `${acc.bankName || 'Bank'} - ${acc.accountNumber || ''} (${acc.accountType || ''})`
          }));
          setBankAccounts(normalized);
        } catch (e) {
          console.error('Failed to load bank accounts:', e);
        } finally {
          setBankLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, paymentForm.paymentMethod]);

  const handleAddPayment = async () => {
    try {
      setAddingPayment(true);
      if (!createdInvoice?._id) return showError('Missing invoice');
      const amt = Number(paymentForm.amount);
      // Calculate due from selected totals with tax
      const grandTotalWithTax = selectedTotals.grand * (1 + Number(invoiceForm.taxRate||0)/100);
      const due = grandTotalWithTax - Number(createdInvoice.amountPaid||0);
      if (amt > due) {
        showWarning('Payment amount cannot exceed amount due');
        return;
      }
      if (!amt || amt <= 0) return showError('Enter a valid amount');
      const payload = {
        paymentMethod: paymentForm.paymentMethod,
        amount: amt,
        bankAccountId: paymentForm.bankAccountId || undefined,
        transactionId: paymentForm.transactionId || undefined,
        reference: paymentForm.reference || undefined,
        notes: paymentForm.notes || undefined,
      };
      if (!['Cash', 'Insurance'].includes(payload.paymentMethod) && !payload.bankAccountId) {
        return showError('Please select a bank account');
      }
      try {
        await billingAPI.addPayment(createdInvoice._id, payload);
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message;
        if (msg && msg.toLowerCase().includes('cannot exceed amount due')) {
          showWarning(msg);
          return;
        }
        throw e;
      }
      showSuccess('Payment recorded');
      setShowWizard(false);
      fetchInvoices(invPage);
    } catch (e) {
      console.error('Failed to add payment:', e);
      showError(e?.response?.data?.message || e?.message || 'Failed to add payment');
    } finally {
      setAddingPayment(false);
    }
  };

  const handlePayLater = () => {
    showInfo('Invoice saved as Pending');
    setShowWizard(false);
    fetchInvoices(invPage);
  };

  const openWizard = () => {
    setShowWizard(true);
    setStep(1);
    setSelectedAppointmentId('');
    setPrescriptions([]);
    setSelectedPrescriptionId('');
    setPreviewData(null);
    setPatientId('');
    setIncludeDoctorCharge(true);
    setSelectedMedicines(new Set());
    setSelectedTests(new Set());
    setSelectedImagingTests(new Set());
    setInvoiceForm({ billingType: 'Patient', taxRate: 18, notes: '' });
    setCreatedInvoice(null);
    setPaymentForm({ paymentMethod: 'Cash', amount: '', bankAccountId: '', transactionId: '', reference: '', notes: '' });
  };

  const openPayModal = (inv) => {
    setSelectedInvoice(inv);
    const due = Number(inv.amountDue ?? (Number(inv.grandTotal||0) - Number(inv.amountPaid||0)));
    setPayForm({ paymentMethod: 'Cash', amount: due, bankAccountId: '', transactionId: '', reference: '', notes: '' });
    setShowPayModal(true);
  };

  const submitListPayment = async () => {
    try {
      if (!selectedInvoice?._id) return;
      setPayLoading(true);
      const amt = Number(payForm.amount);
      const due = Number(selectedInvoice.amountDue ?? (Number(selectedInvoice.grandTotal||0) - Number(selectedInvoice.amountPaid||0)));
      if (amt > due) {
        showWarning('Payment amount cannot exceed amount due');
        return;
      }
      if (!amt || amt <= 0) return showError('Enter a valid amount');
      const payload = {
        paymentMethod: payForm.paymentMethod,
        amount: amt,
        bankAccountId: payForm.bankAccountId || undefined,
        transactionId: payForm.transactionId || undefined,
        reference: payForm.reference || undefined,
        notes: payForm.notes || undefined,
      };
      if (!['Cash', 'Insurance'].includes(payload.paymentMethod) && !payload.bankAccountId) {
        return showError('Please select a bank account');
      }
      try {
        await billingAPI.addPayment(selectedInvoice._id, payload);
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message;
        if (msg && msg.toLowerCase().includes('cannot exceed amount due')) {
          showWarning(msg);
          return;
        }
        throw e;
      }
      showSuccess('Payment recorded');
      setShowPayModal(false);
      fetchInvoices(invPage);
    } catch (e) {
      console.error('Failed to add payment:', e);
      showError(e?.response?.data?.message || e?.message || 'Failed to add payment');
    } finally {
      setPayLoading(false);
    }
  };

  // Load bank accounts for list payment modal when needed
  useEffect(() => {
    const needsBank = !['Cash', 'Insurance'].includes(payForm.paymentMethod);
    if (showPayModal && needsBank) {
      (async () => {
        try {
          setBankLoading(true);
          const res = await bankMasterService.getAll({ status: 'active', limit: 100 });
          const list = Array.isArray(res) ? res : (res?.docs || res?.items || res?.records || []);
          const normalized = list.map(acc => ({
            ...acc,
            displayName: acc.displayName || `${acc.bankName || 'Bank'} - ${acc.accountNumber || ''} (${acc.accountType || ''})`
          }));
          setBankAccounts(normalized);
        } catch (e) {
          console.error('Failed to load bank accounts:', e);
        } finally {
          setBankLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPayModal, payForm.paymentMethod]);

  return (
    <div className="flex-1 p-6 bg-white min-h-screen" style={{ fontFamily: 'Poppins' }}>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-semibold text-black">Billing & Invoices</h2>
          <p className="text-lg text-gray-600 mt-1 font-medium">Manage patient bills and payments</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <input
              className="w-96 border-2 border-gray-200 rounded-xl px-4 py-3 text-lg font-medium focus:outline-none focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] hover:border-[#2D869C] transition-all shadow-sm"
              placeholder="Search invoices..."
              value={invSearch}
              onChange={(e) => setInvSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchInvoices(1); }}
            />
            <button onClick={() => fetchInvoices(1)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#2D869C]">
              <Search size={20} />
            </button>
          </div>
          <button onClick={openWizard} className="px-5 py-2.5 bg-[#2D869C] text-white font-medium rounded-xl hover:bg-[#2D869C]/90 transition-all flex items-center gap-2 shadow-sm hover:shadow-md text-lg">
            <Plus size={20} /> Create New Bill
          </button>
        </div>
      </div>

      {/* Invoices Table Container */}
      <div className="bg-gray-50 rounded-xl p-6 shadow-sm overflow-hidden min-h-[600px] flex flex-col justify-between border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-transparent">
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Invoice no</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Patient Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Total cost</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Paid</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Due</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100/0 space-y-2">
              {invLoading ? (
                <tr><td className="px-6 py-8 text-center text-lg text-gray-500 font-medium" colSpan={8}>Loading...</td></tr>
              ) : invoices.length === 0 ? (
                <tr><td className="px-6 py-8 text-center text-lg text-gray-500 font-medium" colSpan={8}>No invoices found</td></tr>
              ) : (
                (invSearch ? invoices.filter(inv => {
                  const num = (inv.invoiceNumber || inv._id || '').toString().toLowerCase();
                  const pat = inv.patient ? `${inv.patient.firstName || ''} ${inv.patient.lastName || ''}`.toLowerCase() : '';
                  const q = invSearch.toLowerCase();
                  return num.includes(q) || pat.includes(q);
                }) : invoices).map(inv => {
                  const isNew = inv.createdAt && (Date.now() - new Date(inv.createdAt).getTime()) < 24 * 60 * 60 * 1000;
                  const amountDue = Number(inv.amountDue ?? (Number(inv.grandTotal||0)-Number(inv.amountPaid||0)));
                  const amountPaid = Number(inv.amountPaid || 0);
                  const grandTotal = Number(inv.grandTotal || 0);
                  
                  let statusText = 'FULLY PAID';
                  let statusClass = 'bg-[#98EE86] text-green-800';
                  
                  if (amountDue > 0) {
                    if (amountPaid > 0) {
                      statusText = 'PARTIALLY PAID';
                      statusClass = 'bg-[#E7DFF8] text-[#553896]';
                    } else {
                      statusText = 'NOT PAID';
                      statusClass = 'bg-[#F29A9A] text-[#972929]';
                    }
                  }
                  
                  return (
                  <tr key={inv._id} className="hover:bg-white/60 transition-colors bg-white">
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-gray-700">#{inv.invoiceNumber || inv._id}</span>
                        {isNew && <span className="px-2 py-1 text-xs font-bold text-[#3B82F6] bg-[#DBEAFE] rounded">NEW</span>}
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#2D869C]/30">
                          {inv.patient?.avatar ? (
                             <img src={inv.patient.avatar} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-[#9DC9D4]/30 flex items-center justify-center text-[#2D869C] font-semibold text-lg">
                              {inv.patient ? (inv.patient.firstName?.[0] || 'P').toUpperCase() : '?'}
                            </div>
                          )}
                        </div>
                        <span className="text-lg font-medium text-gray-700">{inv.patient ? `${inv.patient.firstName || ''} ${inv.patient.lastName || ''}`.trim() : '-'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-lg font-medium text-gray-600">{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}</td>
                    <td className="px-6 py-5 whitespace-nowrap text-lg font-medium text-gray-700">₹{grandTotal}</td>
                    <td className="px-6 py-5 whitespace-nowrap text-lg font-medium text-gray-700">₹{amountPaid}</td>
                    <td className="px-6 py-5 whitespace-nowrap text-lg font-medium text-gray-700">₹{amountDue}</td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <span className={`px-4 py-2 rounded-full text-md font-semibold uppercase tracking-wider ${statusClass}`}>
                        {statusText}
                      </span>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {amountDue > 0 ? (
                          <button 
                            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[#2D869C] text-white rounded-xl hover:shadow-md transition-all font-medium text-lg w-[140px] hover:bg-[#2D869C]/90" 
                            onClick={() => openPayModal(inv)}
                            title="Collect Payment"
                          >
                            <CreditCard size={18} />
                            <span>Collect</span>
                          </button>
                        ) : (
                          <div className="w-[140px]"></div>
                        )}
                        <button 
                          className="p-2.5 text-gray-400 hover:text-[#2D869C] hover:bg-[#9DC9D4]/20 rounded-xl transition-all" 
                          onClick={() => openInvoicePreview(inv._id)} 
                          title="View Invoice"
                        >
                          <Eye size={22} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination at the bottom right */}
        <div className="flex justify-end items-center gap-2 pt-6">
          <span className="mr-4 text-lg font-medium text-gray-600">Page {invPage} of {invTotalPages}</span>
          
          {invTotalPages > 0 && Array.from({ length: Math.min(5, invTotalPages) }, (_, i) => {
            // Show first 5 pages or current page window
            let pageNum;
            if (invTotalPages <= 5) {
              pageNum = i + 1;
            } else if (invPage <= 3) {
              pageNum = i + 1;
            } else if (invPage >= invTotalPages - 2) {
              pageNum = invTotalPages - 4 + i;
            } else {
              pageNum = invPage - 2 + i;
            }
            
            return (
              <button
                key={pageNum}
                onClick={() => fetchInvoices(pageNum)}
                className={`w-10 h-10 rounded-xl text-lg font-semibold transition-all ${
                  pageNum === invPage 
                    ? 'bg-[#2D869C] text-white shadow-sm' 
                    : 'bg-white border-2 border-[#2D869C]/50 text-[#2D869C] hover:bg-[#9DC9D4]/20'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>
      </div>

      {/* Wizard Modal */}
      {showWizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowWizard(false)}></div>
          <div className="relative bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold text-black">Create Bill</h3>
              <button onClick={() => setShowWizard(false)} className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors text-2xl">✕</button>
            </div>
            {/* Stepper */}
            <div className="bg-[#9DC9D4]/20 rounded-xl p-5 mb-6 border border-[#2D869C]/20">
              <div className="flex items-center justify-between">
                {[1,2,3].map((s, idx) => (
                  <div key={s} className={`flex-1 flex items-center ${idx>0 ? 'ml-2' : ''}`}>
                    <div className={`flex items-center ${s<=step ? 'text-[#2D869C]' : 'text-gray-400'}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${s<=step ? 'bg-[#9DC9D4]/30 border-[#2D869C] text-[#2D869C]' : 'bg-gray-50 border-gray-300'}`}>
                        {s < step ? <CheckCircle2 size={20} /> : <span className="text-lg font-semibold">{s}</span>}
                      </div>
                      <span className="ml-3 text-lg font-medium">
                        {s===1 ? 'Select & Preview' : s===2 ? 'Select Items & Create' : 'Payment'}
                      </span>
                    </div>
                    {s<3 && <div className={`flex-1 mx-4 h-1 rounded ${s<step ? 'bg-[#2D869C]' : 'bg-gray-200'}`}></div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Step 1: Select appointment & prescription, Preview */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-black flex items-center gap-2"><ClipboardList size={20} className="text-[#2D869C]"/> Step 1: Select Appointment & Prescription</h3>
          <div className="text-lg text-gray-500 font-medium">Completed Appointments</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">Appointment</label>
            <select
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-lg font-medium focus:outline-none focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
              value={selectedAppointmentId}
              onChange={(e) => setSelectedAppointmentId(e.target.value)}
              disabled={apptsLoading}
            >
              <option value="">{apptsLoading ? 'Loading appointments...' : 'Select a completed appointment'}</option>
              {appointments.map((apt) => {
                const id = apt._id || apt.id;
                const p = apt.patient || {};
                const patientName = `${p.firstName || p.name || ''} ${p.lastName || ''}`.trim() || p.externalId || 'Patient';
                const when = apt.dateTime || apt.appointmentDate || apt.createdAt;
                const label = `${patientName} • ${when ? new Date(when).toLocaleString() : ''}`;
                return (
                  <option key={id} value={id}>{label}</option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">Prescription</label>
            <select
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-lg font-medium focus:outline-none focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
              value={selectedPrescriptionId}
              onChange={(e) => setSelectedPrescriptionId(e.target.value)}
              disabled={!selectedAppointment || prescriptionsLoading}
            >
              <option value="">{prescriptionsLoading ? 'Loading prescriptions...' : 'Select prescription'}</option>
              {prescriptions.map((pr, idx) => {
                const id = pr._id || pr.id;
                const when = pr.createdAt || pr.date;
                const count = Array.isArray(pr.items) ? pr.items.length : (pr.medicines?.length || 0);
                const displayNumber = prescriptions.length - idx; // Reverse numbering
                return (
                  <option key={id || idx} value={id}>{`#${displayNumber} • ${when ? new Date(when).toLocaleString() : '—'}`}</option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">Billing Type</label>
            <select
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-lg font-medium focus:outline-none focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
              value={invoiceForm.billingType}
              onChange={(e) => setInvoiceForm(prev => ({ ...prev, billingType: e.target.value }))}
            >
              {BILLING_TYPES.map(bt => (
                <option key={bt.value} value={bt.value}>{bt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <FormInput
            label="Tax Rate (%)"
            type="number"
            value={invoiceForm.taxRate}
            onChange={(e) => setInvoiceForm(prev => ({ ...prev, taxRate: e.target.value }))}
            placeholder="e.g. 18"
          />
          <div className="md:col-span-2">
            <FormTextarea
              label="Notes"
              value={invoiceForm.notes}
              onChange={(e) => setInvoiceForm(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
              placeholder="Any notes for the invoice"
            />
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <LoadingButton
            loading={previewLoading}
            onClick={fetchPreview}
            className="px-6 py-3 bg-[#2D869C] text-white font-medium rounded-xl hover:bg-[#2D869C]/90 transition-all shadow-sm hover:shadow-md text-lg"
          >
            🔍 Preview Charges
          </LoadingButton>
        </div>
      </div>

      {/* Doctor charge prompt modal */}
      {showDoctorChargePrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowDoctorChargePrompt(false)}></div>
          <div className="relative bg-white rounded-xl border-2 border-theme-border/30 shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Missing Consultation Fee</h3>
            <p className="text-sm text-gray-600 mb-4">
              No follow-up consultation charge found for this doctor. Do you want to proceed with consultation fee set to ₹0?
            </p>
            <div className="flex justify-end gap-2">
              <button className="px-4 py-2 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors" onClick={() => setShowDoctorChargePrompt(false)}>No</button>
              <LoadingButton loading={previewLoading} onClick={confirmPreviewWithoutDoctorCharge} className="px-4 py-2 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition-colors">
                Yes, Proceed
              </LoadingButton>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Preview with checkboxes and create bill */}
      {step >= 2 && previewData && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
          <h3 className="text-xl font-semibold text-black mb-6 flex items-center gap-2"><FileText size={20} className="text-[#2D869C]"/> Step 2: Select Items to Bill</h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {/* Doctor charges */}
              {previewData.doctorCharges && (
                <div className="border-2 border-theme-border/20 rounded-lg p-4 bg-theme-blue/5">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" className="mt-1 w-4 h-4 accent-theme-border" checked={includeDoctorCharge} onChange={(e) => setIncludeDoctorCharge(e.target.checked)} />
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">Consultation - {previewData.doctorCharges.consultationType}</div>
                      <div className="text-sm text-gray-500">{previewData.doctorCharges.serviceName || 'Doctor Charges'}</div>
                    </div>
                    <div className="font-semibold text-gray-800">₹{Number(previewData.doctorCharges.amount || 0).toFixed(2)}</div>
                  </label>
                </div>
              )}

              {/* Medicines */}
              <div className="border-2 border-theme-border/20 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-theme-border/20 bg-theme-blue/10 rounded-t-lg">
                  <div className="font-semibold text-gray-700">Medicines ({previewData.medicines?.length || 0})</div>
                  <div className="text-sm">
                    <button className="text-theme-border hover:underline mr-3 font-medium" onClick={() => setSelectedMedicines(new Set((previewData.medicines||[]).map(m => (m.medicineId || m._id || '').toString())))}>Select all</button>
                    <button className="text-theme-border hover:underline font-medium" onClick={() => setSelectedMedicines(new Set())}>Clear</button>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {(previewData.medicines||[]).map((m) => {
                    const id = (m.medicineId || m._id || '').toString();
                    const checked = selectedMedicines.has(id);
                    const qty = Number(m.quantity || 1);
                    const price = Number(m.totalPrice || 0);
                    return (
                      <label key={id} className="flex items-center gap-3 px-4 py-3 hover:bg-theme-blue/5 cursor-pointer transition-colors">
                        <input type="checkbox" className="w-4 h-4 accent-theme-border" checked={checked} onChange={(e) => {
                          setSelectedMedicines(prev => {
                            const ns = new Set(prev);
                            if (e.target.checked) ns.add(id); else ns.delete(id);
                            return ns;
                          });
                        }} />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-800">{m.medicineName}</div>
                          <div className="text-xs text-gray-500">
                            {qty} units @ ₹{Number(m.unitPrice).toFixed(2)} = ₹{price.toFixed(2)}
                            {m.instruction && <span className="ml-2 text-gray-400">({m.instruction})</span>}
                          </div>
                        </div>
                        <div className="text-sm font-semibold text-gray-800">₹{price.toFixed(2)}</div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Tests */}
              <div className="border-2 border-theme-border/20 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-theme-border/20 bg-theme-blue/10 rounded-t-lg">
                  <div className="font-semibold text-gray-700">Tests ({previewData.tests?.length || 0})</div>
                  <div className="text-sm">
                    <button className="text-theme-border hover:underline mr-3 font-medium" onClick={() => setSelectedTests(new Set((previewData.tests||[]).map(t => (t.testId || t._id || '').toString())))}>Select all</button>
                    <button className="text-theme-border hover:underline font-medium" onClick={() => setSelectedTests(new Set())}>Clear</button>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {(previewData.tests||[]).map((t) => {
                    const id = (t.testId || t._id || '').toString();
                    const checked = selectedTests.has(id);
                    const price = Number(t.totalPrice || 0);
                    return (
                      <label key={id} className="flex items-center gap-3 px-4 py-3 hover:bg-theme-blue/5 cursor-pointer transition-colors">
                        <input type="checkbox" className="w-4 h-4 accent-theme-border" checked={checked} onChange={(e) => {
                          setSelectedTests(prev => {
                            const ns = new Set(prev);
                            if (e.target.checked) ns.add(id); else ns.delete(id);
                            return ns;
                          });
                        }} />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-800">{t.testName}</div>
                          <div className="text-xs text-gray-500">Qty: {t.quantity || 1} × ₹{Number(t.unitPrice).toFixed(2)}</div>
                        </div>
                        <div className="text-sm font-semibold text-gray-800">₹{price.toFixed(2)}</div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Imaging Tests */}
              <div className="border-2 border-theme-border/20 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-theme-border/20 bg-theme-blue/10 rounded-t-lg">
                  <div className="font-semibold text-gray-700">Imaging Tests ({previewData.imagingTests?.length || 0})</div>
                  <div className="text-sm">
                    <button className="text-theme-border hover:underline mr-3 font-medium" onClick={() => setSelectedImagingTests(new Set((previewData.imagingTests||[]).map(t => (t.imagingTestId || t._id || '').toString())))}>Select all</button>
                    <button className="text-theme-border hover:underline font-medium" onClick={() => setSelectedImagingTests(new Set())}>Clear</button>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {(previewData.imagingTests||[]).map((t) => {
                    const id = (t.imagingTestId || t._id || '').toString();
                    const checked = selectedImagingTests.has(id);
                    const price = Number(t.totalPrice || 0);
                    return (
                      <label key={id} className="flex items-center gap-3 px-4 py-3 hover:bg-theme-blue/5 cursor-pointer transition-colors">
                        <input type="checkbox" className="w-4 h-4 accent-theme-border" checked={checked} onChange={(e) => {
                          setSelectedImagingTests(prev => {
                            const ns = new Set(prev);
                            if (e.target.checked) ns.add(id); else ns.delete(id);
                            return ns;
                          });
                        }} />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-800">{t.testName}</div>
                          <div className="text-xs text-gray-500">
                            {t.modality && <span className="mr-2">({t.modality})</span>}
                            Qty: {t.quantity || 1} × ₹{Number(t.unitPrice).toFixed(2)}
                          </div>
                        </div>
                        <div className="text-sm font-semibold text-gray-800">₹{price.toFixed(2)}</div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Summary & action */}
            <div className="bg-theme-blue/10 rounded-xl p-5 h-fit border-2 border-theme-border/20">
              <div className="text-sm font-semibold text-gray-700 mb-3">Summary</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-700"><span>Consultation</span><span>₹{selectedTotals.doctor.toFixed(2)}</span></div>
                <div className="flex justify-between text-gray-700"><span>Medicines</span><span>₹{selectedTotals.medicines.toFixed(2)}</span></div>
                <div className="flex justify-between text-gray-700"><span>Tests</span><span>₹{selectedTotals.tests.toFixed(2)}</span></div>
                <div className="flex justify-between text-gray-700"><span>Imaging Tests</span><span>₹{selectedTotals.imagingTests.toFixed(2)}</span></div>
                <div className="border-t border-theme-border/30 pt-2 flex justify-between font-semibold text-gray-800"><span>Subtotal</span><span>₹{selectedTotals.grand.toFixed(2)}</span></div>
                <div className="flex justify-between text-gray-600"><span>Tax ({Number(invoiceForm.taxRate||0)}%)</span><span>₹{(selectedTotals.grand * (Number(invoiceForm.taxRate||0)/100)).toFixed(2)}</span></div>
                <div className="border-t border-theme-border/30 pt-2 flex justify-between font-bold text-gray-900 text-lg"><span>Grand Total</span><span>₹{(selectedTotals.grand * (1 + Number(invoiceForm.taxRate||0)/100)).toFixed(2)}</span></div>
              </div>
              <div className="mt-4">
                <LoadingButton loading={creatingInvoice} onClick={handleCreateFromSelection} className="w-full px-4 py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors">
                  🧾 Create Bill
                </LoadingButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Payment */}
      {step >= 3 && createdInvoice && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
          <h3 className="text-xl font-semibold text-black mb-6 flex items-center gap-2"><CreditCard size={20} className="text-[#2D869C]"/> Step 3: Record Payment</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <div className="bg-theme-blue/10 rounded-xl p-4 border-2 border-theme-border/20">
                <div className="text-sm font-semibold text-gray-700 mb-2">Invoice Summary</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-xs text-gray-500"><span>Invoice #</span><span>{createdInvoice.invoiceNumber || createdInvoice._id}</span></div>
                  <div className="border-t border-theme-border/30 pt-2 space-y-1">
                    <div className="flex justify-between text-gray-700"><span>Consultation</span><span>₹{selectedTotals.doctor.toFixed(2)}</span></div>
                    <div className="flex justify-between text-gray-700"><span>Medicines</span><span>₹{selectedTotals.medicines.toFixed(2)}</span></div>
                    <div className="flex justify-between text-gray-700"><span>Tests</span><span>₹{selectedTotals.tests.toFixed(2)}</span></div>
                    <div className="flex justify-between text-gray-700"><span>Imaging Tests</span><span>₹{selectedTotals.imagingTests.toFixed(2)}</span></div>
                    <div className="border-t border-theme-border/30 pt-1 flex justify-between text-gray-700"><span>Subtotal</span><span>₹{selectedTotals.grand.toFixed(2)}</span></div>
                    <div className="flex justify-between text-gray-600"><span>Tax ({Number(invoiceForm.taxRate||0)}%)</span><span>₹{(selectedTotals.grand * (Number(invoiceForm.taxRate||0)/100)).toFixed(2)}</span></div>
                    <div className="border-t border-theme-border/30 pt-2 flex justify-between font-bold text-gray-900"><span>Grand Total</span><span>₹{(selectedTotals.grand * (1 + Number(invoiceForm.taxRate||0)/100)).toFixed(2)}</span></div>
                  </div>
                  <div className="border-t border-theme-border/30 pt-2 space-y-1">
                    <div className="flex justify-between text-gray-700"><span>Paid</span><span>₹{Number(createdInvoice.amountPaid || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between font-semibold text-lg text-gray-900"><span>Due</span><span>₹{((selectedTotals.grand * (1 + Number(invoiceForm.taxRate||0)/100)) - Number(createdInvoice.amountPaid || 0)).toFixed(2)}</span></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Payment Method</label>
                <select
                  className="w-full border-2 border-theme-border/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-theme-border focus:border-theme-border text-gray-700"
                  value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  {PAYMENT_METHOD_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <FormInput
                label="Amount"
                type="number"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="Enter amount to collect now"
              />

              {!['Cash', 'Insurance'].includes(paymentForm.paymentMethod) && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Bank Account</label>
                  <select
                    className="w-full border-2 border-theme-border/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-theme-border focus:border-theme-border text-gray-700"
                    value={paymentForm.bankAccountId}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, bankAccountId: e.target.value }))}
                    disabled={bankLoading}
                  >
                    <option value="">{bankLoading ? 'Loading accounts...' : 'Select bank account'}</option>
                    {bankAccounts.map(acc => (
                      <option key={acc._id} value={acc._id}>{acc.displayName || `${acc.bankName} - ${acc.accountNumber}`}</option>
                    ))}
                  </select>
                </div>
              )}

              <FormInput
                label={paymentForm.paymentMethod === 'Cheque' ? 'Cheque Number' : 'Transaction ID'}
                value={paymentForm.transactionId}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, transactionId: e.target.value }))}
                placeholder="Optional transaction reference"
              />
              <FormInput
                label="Reference"
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
                placeholder="Optional notes/reference"
              />
              <div className="md:col-span-2">
                <FormTextarea
                  label="Notes"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  placeholder="Payment notes"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-4 gap-3">
            <button onClick={handlePayLater} className="px-4 py-2 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors">Pay Later</button>
            <LoadingButton loading={addingPayment} onClick={handleAddPayment} className="px-4 py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors">
              💳 Add Payment
            </LoadingButton>
          </div>
        </div>
      )}
          </div>
        </div>
      )}

      {/* Pay from list Modal */}
      {showPayModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowPayModal(false)}></div>
          <div className="relative bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold text-black">Collect Payment</h3>
              <button onClick={() => setShowPayModal(false)} className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors text-2xl">✕</button>
            </div>
            {selectedInvoice && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-theme-blue/10 rounded-xl p-4 md:col-span-2 border-2 border-theme-border/20">
                  <div className="text-sm font-semibold text-gray-700 mb-2">Invoice Summary</div>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div><span className="text-gray-500">Invoice #:</span> <span className="font-semibold text-gray-800">{selectedInvoice.invoiceNumber || selectedInvoice._id}</span></div>
                    <div><span className="text-gray-500">Due:</span> <span className="font-bold text-red-600">₹{Number(selectedInvoice.amountDue ?? (Number(selectedInvoice.grandTotal||0)-Number(selectedInvoice.amountPaid||0))).toFixed(2)}</span></div>
                    <div><span className="text-gray-500">Patient:</span> <span className="text-gray-800">{selectedInvoice.patient ? `${selectedInvoice.patient.firstName || ''} ${selectedInvoice.patient.lastName || ''}`.trim() : '-'}</span></div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Payment Method</label>
                  <select
                    className="w-full border-2 border-theme-border/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-theme-border focus:border-theme-border text-gray-700"
                    value={payForm.paymentMethod}
                    onChange={(e) => setPayForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  >
                    {PAYMENT_METHOD_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <FormInput
                  label="Amount"
                  type="number"
                  value={payForm.amount}
                  onChange={(e) => setPayForm(prev => ({ ...prev, amount: e.target.value }))}
                />

                {!['Cash', 'Insurance'].includes(payForm.paymentMethod) && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Bank Account</label>
                    <select
                      className="w-full border-2 border-theme-border/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-theme-border focus:border-theme-border text-gray-700"
                      value={payForm.bankAccountId}
                      onChange={(e) => setPayForm(prev => ({ ...prev, bankAccountId: e.target.value }))}
                    >
                      <option value="">Select bank account</option>
                      {bankAccounts.map(acc => (
                        <option key={acc._id} value={acc._id}>{acc.displayName || `${acc.bankName} - ${acc.accountNumber}`}</option>
                      ))}
                    </select>
                  </div>
                )}

                <FormInput
                  label={payForm.paymentMethod === 'Cheque' ? 'Cheque Number' : 'Transaction ID'}
                  value={payForm.transactionId}
                  onChange={(e) => setPayForm(prev => ({ ...prev, transactionId: e.target.value }))}
                />
                <FormInput
                  label="Reference"
                  value={payForm.reference}
                  onChange={(e) => setPayForm(prev => ({ ...prev, reference: e.target.value }))}
                />
                <div className="md:col-span-2">
                  <FormTextarea
                    label="Notes"
                    value={payForm.notes}
                    onChange={(e) => setPayForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                  />
                </div>

                <div className="md:col-span-2 flex justify-end gap-3">
                  <button className="px-4 py-2 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors" onClick={() => setShowPayModal(false)}>Cancel</button>
                  <LoadingButton loading={payLoading} onClick={submitListPayment} className="px-4 py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors">Collect Payment</LoadingButton>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invoice Preview Modal */}
      {showInvoicePreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowInvoicePreview(false)}></div>
          <div className="relative bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-7xl max-h-[95vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0">
              <h3 className="text-2xl font-semibold text-black">Invoice Preview</h3>
              <button onClick={() => setShowInvoicePreview(false)} className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors text-2xl">✕</button>
            </div>
            <div className="p-4 overflow-y-auto">
            {invoicePreviewLoading ? (
              <div className="p-6 text-sm text-gray-500">Loading preview...</div>
            ) : invoicePreview ? (
              <div className="space-y-4">
                {/* Invoice Header */}
                <div className="bg-[#9DC9D4]/20 rounded-xl p-5 border-2 border-[#2D869C]/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-500 font-medium">Invoice #</div>
                      <div className="text-xl font-semibold text-gray-800">{invoicePreview.invoice.invoiceNumber}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500 font-medium">Invoice Date & Time</div>
                      <div className="text-lg text-gray-800 font-medium">{invoicePreview.invoice.date ? new Date(invoicePreview.invoice.date).toLocaleString('en-IN', { hour12: true }) : '-'}</div>
                    </div>
                  </div>
                </div>

                {/* Patient Information */}
                <div className="bg-white rounded-xl p-5 border-2 border-[#2D869C]/20">
                  <div className="text-lg font-semibold text-gray-800 mb-3">Patient Information</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-base">
                    <div><span className="text-gray-600 font-medium">IP Registration Number:</span> <span className="font-semibold text-gray-800">{invoicePreview.patient?.externalId || '-'}</span></div>
                    <div><span className="text-gray-600 font-medium">Name:</span> <span className="font-semibold text-gray-800">{invoicePreview.patient?.name || '-'}</span></div>
                    <div><span className="text-gray-600 font-medium">Phone:</span> <span className="font-semibold text-gray-800">{invoicePreview.patient?.phone || '-'}</span></div>
                    <div className="md:col-span-2"><span className="text-gray-600 font-medium">Email:</span> <span className="font-semibold text-gray-800">{invoicePreview.patient?.email || '-'}</span></div>
                    <div className="md:col-span-2"><span className="text-gray-600 font-medium">Address:</span> <span className="font-semibold text-gray-800">{invoicePreview.patient?.address || '-'}</span></div>
                  </div>
                </div>

                {/* Doctor Information */}
                <div className="bg-white rounded-xl p-5 border-2 border-[#2D869C]/20">
                  <div className="text-lg font-semibold text-gray-800 mb-3">Doctor (Appointment)</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-base">
                    <div><span className="text-gray-600 font-medium">Name:</span> <span className="font-semibold text-gray-800">{invoicePreview.doctor?.name || '-'}</span></div>
                    <div><span className="text-gray-600 font-medium">Reg. No.:</span> <span className="font-semibold text-gray-800">{invoicePreview.doctor?.registrationNumber || '-'}</span></div>
                    <div><span className="text-gray-600 font-medium">Department:</span> <span className="font-semibold text-gray-800">{invoicePreview.doctor?.department || '-'}</span></div>
                    <div><span className="text-gray-600 font-medium">Specialization:</span> <span className="font-semibold text-gray-800">{invoicePreview.doctor?.specialization || '-'}</span></div>
                    <div className="md:col-span-2"><span className="text-gray-600 font-medium">Appt. Date & Time:</span> <span className="font-semibold text-gray-800">{invoicePreview.appointment?.dateTime ? new Date(invoicePreview.appointment.dateTime).toLocaleString('en-IN', { hour12: true }) : '-'}</span></div>
                  </div>
                </div>

                {/* Invoice Details */}
                <div className="bg-white rounded-xl p-5 border-2 border-[#2D869C]/20">
                  <div className="text-lg font-semibold text-gray-800 mb-4">Invoice Details</div>
                  {invoicePreview.items?.doctorCharges && (
                    <div className="flex items-center justify-between text-base mb-4 p-3 bg-[#9DC9D4]/10 rounded-xl">
                      <div className="text-gray-800 font-medium">Consultation - {invoicePreview.items.doctorCharges.consultationType}</div>
                      <div className="font-semibold text-gray-800">₹{Number(invoicePreview.items.doctorCharges.amount||0).toFixed(2)}</div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <div className="text-sm text-gray-700 mb-2 font-semibold">Medicines</div>
                      <div className="border-2 border-[#2D869C]/20 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-[#9DC9D4]/20">
                            <tr>
                              <th className="px-3 py-2.5 text-left text-gray-700 font-semibold">Medicine</th>
                              <th className="px-3 py-2.5 text-right text-gray-700 font-semibold">Qty</th>
                              <th className="px-3 py-2.5 text-right text-gray-700 font-semibold">Unit</th>
                              <th className="px-3 py-2.5 text-right text-gray-700 font-semibold">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(invoicePreview.items?.medicines||[]).map((m, i) => (
                              <tr key={i} className="border-t border-gray-100">
                                <td className="px-3 py-2 text-gray-700 font-medium">{m.name}</td>
                                <td className="px-3 py-2 text-right text-gray-700">{m.quantity}</td>
                                <td className="px-3 py-2 text-right text-gray-700">₹{Number(m.unitPrice||0).toFixed(2)}</td>
                                <td className="px-3 py-2 text-right text-gray-800 font-semibold">₹{(Number(m.unitPrice||0) * Number(m.quantity||1)).toFixed(2)}</td>
                              </tr>
                            ))}
                            {(!invoicePreview.items?.medicines || invoicePreview.items.medicines.length===0) && (
                              <tr><td colSpan={4} className="px-3 py-3 text-gray-500 font-medium">No medicines</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-700 mb-2 font-semibold">Tests</div>
                      <div className="border-2 border-[#2D869C]/20 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-[#9DC9D4]/20">
                            <tr>
                              <th className="px-3 py-2.5 text-left text-gray-700 font-semibold">Test</th>
                              <th className="px-3 py-2.5 text-right text-gray-700 font-semibold">Qty</th>
                              <th className="px-3 py-2.5 text-right text-gray-700 font-semibold">Unit</th>
                              <th className="px-3 py-2.5 text-right text-gray-700 font-semibold">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(invoicePreview.items?.tests||[]).map((t, i) => (
                              <tr key={i} className="border-t border-gray-100">
                                <td className="px-3 py-2 text-gray-700 font-medium">{t.name}</td>
                                <td className="px-3 py-2 text-right text-gray-700">{t.quantity}</td>
                                <td className="px-3 py-2 text-right text-gray-700">₹{Number(t.unitPrice||0).toFixed(2)}</td>
                                <td className="px-3 py-2 text-right text-gray-800 font-semibold">₹{(Number(t.unitPrice||0) * Number(t.quantity||1)).toFixed(2)}</td>
                              </tr>
                            ))}
                            {(!invoicePreview.items?.tests || invoicePreview.items.tests.length===0) && (
                              <tr><td colSpan={4} className="px-3 py-3 text-gray-500 font-medium">No tests</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Imaging Tests */}
                  {invoicePreview.items?.imagingTests && invoicePreview.items.imagingTests.length > 0 && (
                    <div className="mt-5">
                      <div className="text-sm text-gray-700 mb-2 font-semibold">Imaging Tests</div>
                      <div className="border-2 border-[#2D869C]/20 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-[#9DC9D4]/20">
                            <tr>
                              <th className="px-3 py-2.5 text-left text-gray-700 font-semibold">Test Name</th>
                              <th className="px-3 py-2.5 text-left text-gray-700 font-semibold">Modality</th>
                              <th className="px-3 py-2.5 text-left text-gray-700 font-semibold">Body Part</th>
                              <th className="px-3 py-2.5 text-right text-gray-700 font-semibold">Qty</th>
                              <th className="px-3 py-2.5 text-right text-gray-700 font-semibold">Unit Price</th>
                              <th className="px-3 py-2.5 text-right text-gray-700 font-semibold">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoicePreview.items.imagingTests.map((img, i) => (
                              <tr key={i} className="border-t border-gray-100">
                                <td className="px-3 py-2 text-gray-700 font-medium">{img.name}</td>
                                <td className="px-3 py-2 text-gray-700">{img.modality || '-'}</td>
                                <td className="px-3 py-2 text-gray-700">{img.bodyPart || '-'}</td>
                                <td className="px-3 py-2 text-right text-gray-700">{img.quantity}</td>
                                <td className="px-3 py-2 text-right text-gray-700">₹{Number(img.unitPrice||0).toFixed(2)}</td>
                                <td className="px-3 py-2 text-right text-gray-800 font-semibold">₹{(Number(img.unitPrice||0) * Number(img.quantity||1)).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="mt-5 text-base space-y-2.5 border-t-2 border-[#2D869C]/30 pt-4">
                    <div className="flex justify-between text-gray-700 font-medium"><span>Total Amount</span><span>₹{Number(invoicePreview.invoice.totals.subtotal||0).toFixed(2)}</span></div>
                    <div className="flex justify-between text-gray-700 font-medium">
                      <span>Tax (GST) ({invoicePreview.items?.doctorCharges?.taxRate || invoicePreview.items?.tests?.[0]?.taxRate || invoicePreview.items?.medicines?.[0]?.taxRate || invoicePreview.items?.imagingTests?.[0]?.taxRate || 18}%)</span>
                      <span>₹{Number(invoicePreview.invoice.totals.tax||0).toFixed(2)}</span>
                    </div>
                    {Number(invoicePreview.invoice.totals.discount||0) > 0 && (
                      <div className="flex justify-between text-red-600 font-medium"><span>Discount</span><span>-₹{Number(invoicePreview.invoice.totals.discount||0).toFixed(2)}</span></div>
                    )}
                    <div className="flex justify-between text-gray-700 font-medium"><span>Paid Amount</span><span>₹{Number(invoicePreview.invoice.totals.amountPaid||0).toFixed(2)}</span></div>
                    <div className="border-t-2 border-[#2D869C]/30 pt-3 mt-3 flex justify-between font-bold text-gray-900 text-xl"><span>Grand Total</span><span>₹{Number(invoicePreview.invoice.totals.grandTotal||0).toFixed(2)}</span></div>
                  </div>
                </div>

                {/* Payment Information */}
                <div className="bg-white rounded-xl p-5 border-2 border-[#2D869C]/20">
                  <div className="text-lg font-semibold text-gray-800 mb-3">Payment Information</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-base">
                    <div><span className="text-gray-600 font-medium">Mode:</span> <span className="font-semibold text-gray-800">{invoicePreview.payment?.mode || '-'}</span></div>
                    <div><span className="text-gray-600 font-medium">Due:</span> <span className="font-bold text-red-600">₹{Number(invoicePreview.payment?.due||0).toFixed(2)}</span></div>
                    <div><span className="text-gray-600 font-medium">Collected By:</span> <span className="font-semibold text-gray-800">{invoicePreview.payment?.collectedBy?.name || '-'}</span></div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-3">
                  <label className="inline-flex items-center text-base select-none text-gray-700 cursor-pointer font-medium">
                    <input
                      type="checkbox"
                      className="mr-3 h-5 w-5 accent-[#2D869C]"
                      checked={duplicateCopy}
                      onChange={(e) => setDuplicateCopy(e.target.checked)}
                    />
                    Duplicate copy (watermark)
                  </label>
                  <div className="flex justify-end gap-3">
                    <button
                      className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-100 transition-colors text-base"
                      onClick={() => printInvoice(invoicePreview, { duplicate: duplicateCopy })}
                      title={duplicateCopy ? "Print (duplicate watermark)" : "Print"}
                    >
                      Print
                    </button>
                    <button
                      className="px-6 py-3 bg-[#2D869C] text-white font-medium rounded-xl hover:bg-[#2D869C]/90 transition-colors text-base shadow-sm hover:shadow-md"
                      onClick={() => downloadInvoicePDF(invoicePreview, { duplicate: duplicateCopy })}
                      title={duplicateCopy ? "Download Duplicate PDF" : "Download PDF"}
                    >
                      Download PDF
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 text-sm text-gray-500">No data</div>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
